import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { getDashboardStats, getRecentReviews, getClients } from '@/services/api';
import { toast } from 'sonner';
import { Search, Plus, Users, FileText, Star, ChevronRight, AlertCircle, MapPin } from 'lucide-react';

export default function DashboardPage() {
  const { user, isSubscribed } = useAuth();
  const [stats, setStats] = useState({ total_clients: 0, total_reviews: 0, my_reviews: 0 });
  const [recentReviews, setRecentReviews] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isSubscribed) {
        setLoading(false);
        return;
      }
      
      try {
        const [statsData, reviewsData] = await Promise.all([
          getDashboardStats(),
          getRecentReviews(5)
        ]);
        setStats(statsData);
        setRecentReviews(reviewsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status !== 403) {
          toast.error('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isSubscribed]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !isSubscribed) return;
    
    setSearching(true);
    try {
      const results = await getClients(searchQuery);
      setSearchResults(results);
      if (results.length === 0) {
        toast.info('No clients found. Try a different search or add a new client.');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isSubscribed) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="border-[#D97757] border-2 bg-[#D97757]/5" data-testid="subscription-required-card">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-[#D97757] mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
                Subscription Required
              </h2>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                To access client reviews and the full platform, please activate your subscription.
              </p>
              <Link to="/subscription">
                <Button className="h-11 px-8 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]" data-testid="activate-subscription-btn">
                  Activate Subscription
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900 mb-2">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-stone-600">Here's what's happening in your community</p>
        </div>

        {/* Quick Search */}
        <Card className="mb-8 border-stone-100 shadow-sm animate-slide-up" data-testid="quick-search-card">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search clients by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 pl-10 rounded-lg border-stone-200"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-12 px-6 rounded-lg bg-stone-900 text-white hover:bg-stone-800"
                disabled={searching}
                data-testid="search-btn"
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
              <Link to="/clients">
                <Button 
                  type="button"
                  variant="outline" 
                  className="h-12 px-6 rounded-lg border-stone-200"
                  data-testid="add-client-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Client
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card className="mb-8 border-stone-100 shadow-sm animate-slide-up" data-testid="search-results-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-lg text-stone-900">
                Search Results ({searchResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-stone-100">
                {searchResults.map((client) => (
                  <Link 
                    key={client.id}
                    to={`/clients/${client.id}`}
                    className="flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                    data-testid={`search-result-${client.id}`}
                  >
                    <div>
                      <p className="font-medium text-stone-900">
                        {client.first_name} {client.last_name || (client.last_initial ? `${client.last_initial}.` : '')}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-stone-500">
                        {client.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {client.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className={`w-4 h-4 ${client.avg_rating > 0 ? 'fill-[#D97757] text-[#D97757]' : 'text-stone-300'}`} />
                          <span className="font-medium text-stone-900">{client.avg_rating > 0 ? client.avg_rating.toFixed(1) : 'N/A'}</span>
                        </div>
                        <p className="text-xs text-stone-500">{client.review_count} reviews</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { label: 'Total Clients', value: stats.total_clients, icon: Users, color: 'bg-stone-900', link: '/clients' },
            { label: 'Total Reviews', value: stats.total_reviews, icon: FileText, color: 'bg-[#D97757]', link: '/clients' },
            { label: 'My Reviews', value: stats.my_reviews, icon: Star, color: 'bg-[#5F8D4E]', link: '/my-reviews' },
          ].map((stat, index) => (
            <Link 
              key={stat.label}
              to={stat.link}
              className="block"
            >
              <Card 
                className={`border-stone-100 shadow-sm animate-slide-up stagger-${index + 1} hover:shadow-md hover:border-stone-200 transition-all cursor-pointer`}
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-stone-600 text-sm mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-stone-900">{loading ? '—' : stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-xs text-stone-400 mt-2 flex items-center gap-1">
                    View all <ChevronRight className="w-3 h-3" />
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Reviews */}
        <Card className="border-stone-100 shadow-sm animate-slide-up stagger-4" data-testid="recent-reviews-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl text-stone-900">Recent Reviews</CardTitle>
            <Link to="/clients">
              <Button variant="ghost" className="text-[#D97757] hover:text-[#C06040] hover:bg-[#D97757]/10">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-stone-500">Loading...</div>
            ) : recentReviews.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-stone-500 mb-4">No reviews yet. Be the first to add one!</p>
                <Link to="/clients">
                  <Button className="h-10 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]" data-testid="first-review-btn">
                    Add Your First Review
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {recentReviews.map((review) => (
                  <div 
                    key={review.id}
                    className="flex items-start gap-4 p-4 rounded-xl bg-stone-50 border border-stone-100"
                    data-testid={`recent-review-${review.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-stone-600 font-medium text-sm">
                        {review.is_anonymous ? '?' : review.reviewer_name?.charAt(0) || 'A'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="font-medium text-stone-900 truncate">
                          {review.is_anonymous ? 'Anonymous' : review.reviewer_name}
                        </p>
                        <span className="text-xs text-stone-500 flex-shrink-0">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(i => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i <= Math.round(review.overall_rating) ? 'fill-[#D97757] text-[#D97757]' : 'text-stone-300'}`} 
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-stone-700">{review.overall_rating.toFixed(1)}</span>
                      </div>
                      {review.general_notes && (
                        <p className="text-sm text-stone-600 line-clamp-2">{review.general_notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
